import { useEffect, useRef } from 'react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const VARIANT_STYLES: Record<string, { bg: string; hover: string; text: string }> = {
  primary: { bg: '#2563eb', hover: '#1d4ed8', text: '#fff' },
  danger: { bg: '#dc2626', hover: '#b91c1c', text: '#fff' },
  warning: { bg: '#d97706', hover: '#b45309', text: '#fff' },
};

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const variant = VARIANT_STYLES[confirmVariant];

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (isOpen) {
      if (!el.open) el.showModal();
    } else {
      if (el.open) el.close();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      onCancel={onCancel}
      style={{
        border: 'none',
        borderRadius: '12px',
        padding: 0,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '440px',
        width: '90vw',
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: -1,
        }}
        onClick={onCancel}
      />
      <div style={{ padding: '28px 28px 24px' }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
          {title}
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#4b5563', lineHeight: 1.6 }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: '8px 18px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '8px 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: isLoading ? '#9ca3af' : variant.bg,
              color: variant.text,
              fontSize: '14px',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              minWidth: '90px',
            }}
          >
            {isLoading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
